import classNames from 'classnames'
import React, { useMemo, useState, useEffect, useRef } from 'react'

import { EFilterKeys } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { CheckboxGroup, ChipGroup, ColorFilterGroup, Skeleton } from '@src/quince-components/src/atoms'
import siteStateManager from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

import { FilterCollapseGroup } from '../../components/FilterCollapseGroup'
import { FilterDropdown } from '../../components/FilterDropdown'
import { SORT_OPTIONS, FILTER_DROPDOWN_TYPES, MAX_FILTERS } from '../../constants/constants'
import {
  FALLBACK_COLOR,
  CLEAR_ALL_TEXT,
  FILTER_TYPES,
  SORT_BUTTON_TEXT,
  CLEAR_ALL_THRESHOLD,
} from '../../constants/filterSection.constants'
import { filterListOnExp, useShouldHideDeptAndCategoryFilter } from '../../filterListOnExp'
import { useFilterState } from '../../hooks/useFilterState'
import { FilterWithSidebarProps, FilterItem, UpdateQueryParams } from '../filterSection.interface'
import { createCheckboxOptions, excludeDepartmentFilter } from '../filterUtils'

import styles from './filterWithSidebar.module.scss'

interface FilterWithSidebarItemCountAndSortProps {
  noOfItems: number
  sortBy?: string
  skipSorting?: boolean
  updateQuery: (params: UpdateQueryParams) => void
  className?: string
}

export const FilterWithSidebarItemCountAndSort: React.FC<FilterWithSidebarItemCountAndSortProps> = ({
  noOfItems,
  sortBy = '',
  skipSorting = false,
  updateQuery,
  className,
}) => {
  // Handle sort change
  const handleSortChange = (value: string) => {
    updateQuery({ sort: { type: 'toggle', value } })
  }

  // Render the sort dropdown component
  const renderSortDropdown = () => {
    if (skipSorting) return null

    return (
      <FilterDropdown
        type={FILTER_DROPDOWN_TYPES.SORT}
        label={
          <span>
            {SORT_BUTTON_TEXT}
            {`: ${SORT_OPTIONS.find(opt => opt.value === sortBy)?.label}`}
          </span>
        }
        value={sortBy}
        onChange={handleSortChange}
        placement="bottomRight"
        menuClassName={styles.sortDropdownMenu}
        buttonClassName={styles.sortDropdown}
        isMobile={false}
        isActive={sortBy !== ''}
        onTriggerClick={(_, isOpen) => {
          if (isOpen) {
            trackEvent(TELEMETRY_EVENT_NAMES.SORT_OPENED, {})
          }
        }}
      />
    )
  }

  return (
    <div className={classNames(styles.itemCountAndSortContainer, className)}>
      <div className={styles.itemCount}>{noOfItems} items</div>
      <div className={styles.sortContainer}>{renderSortDropdown()}</div>
    </div>
  )
}

export const FilterWithSidebar: React.FC<FilterWithSidebarProps> = props => {
  const {
    className,
    filters: serverFilters = [],
    selectedFilters = {},
    hideFilters = false,
    collectionSlug,
    updateQuery = (_params: UpdateQueryParams) => {
      /* no-op */
    },
    showShimmer = false,
  } = props

  const [activeFilters, setActiveFilters] = useState<string[]>([])

  // Use the custom hook for filter state management
  const {
    selectedFiltersState,
    selectedFilterChips,
    totalSelectedFilters,
    clearAllFilters,
    handleCheckboxFilterChange,
    handleColorChange,
    handleChipDismissByLabel,
  } = useFilterState(selectedFilters, updateQuery)

  const isScrollingDown = useAppSelector<boolean>(siteStateManager, siteStateManager.getIsScrollingDown)

  // Determine if header is visible
  const isHeaderVisible = !isScrollingDown

  const hideDeptAndCategoryFilter = useShouldHideDeptAndCategoryFilter()

  const filterSectionRef = useRef<HTMLDivElement>(null)

  const filters = useMemo(() => {
    const filteredList = filterListOnExp(
      excludeDepartmentFilter(serverFilters, collectionSlug),
      hideDeptAndCategoryFilter,
    )
      .filter(item => item.values.length > 1)
      .slice(0, MAX_FILTERS)

    return filteredList
  }, [serverFilters, hideDeptAndCategoryFilter, collectionSlug])

  const activeFilterNames = useMemo(() => filters.map(filter => filter.name), [filters])

  useEffect(() => {
    setActiveFilters(activeFilterNames)
  }, [activeFilterNames])

  useEffect(() => {
    if (filterSectionRef.current) {
      filterSectionRef.current.scrollTo({ top: 0, behavior: 'auto' })
    }
  }, [collectionSlug])

  const handleFilterCollapseChange = (activeKeys: string[]) => {
    setActiveFilters(activeKeys)
  }

  // If hideFilters is true or there are no filters, don't render anything
  if (hideFilters || !filters.length) {
    return null
  }

  // Helper function to render a checkbox filter
  const renderCheckboxFilter = (filter: FilterItem) => {
    const state = selectedFiltersState[filter.name] ?? []

    return (
      <CheckboxGroup
        key={`sidebar-${filter.name}-${state.length}`}
        options={createCheckboxOptions(filter.values, state)}
        onChange={selectedOptions => {
          handleCheckboxFilterChange(filter, selectedOptions)
        }}
        direction="column"
        maxVisible={8}
        className={styles.checkboxGroup}
        showMoreButtonClassName={styles.showMoreButtonDrawer}
      />
    )
  }

  // Helper function to render a color filter
  const renderColorFilter = (filter: FilterItem) => {
    const colorOptions = filter.values.map(value => {
      if (typeof value === 'string') {
        return {
          value,
          hex: FALLBACK_COLOR,
          label: value,
        }
      }

      const colorValue = value
      return {
        value: colorValue.name,
        hex: colorValue.hex ?? FALLBACK_COLOR,
        label: colorValue.name,
        imgURL: colorValue.imgURL ?? undefined,
      }
    })

    return (
      <ColorFilterGroup
        key={`sidebar-color-group-${selectedFiltersState[filter.name]?.length}`}
        options={colorOptions}
        selectedValues={selectedFiltersState[filter.name] ?? []}
        onChange={value => {
          handleColorChange(filter, value)
        }}
        direction="horizontal"
        circleClassName={styles.colorFilterCircle}
        columns={5}
        circleSize={2.2}
        rowGap={1.6}
        columnGap={0}
        className={styles.colorFilterGroupDrawer}
      />
    )
  }

  // Unified function to render filter component
  const renderFilterComponent = (filter: FilterItem) => {
    const { config, name } = filter
    const filterType = config.type.toLowerCase()
    const filterName = name.toLowerCase()

    switch (filterType) {
      case FILTER_TYPES.CHECKBOX:
      case FILTER_TYPES.CARD:
        if (filterName === EFilterKeys.COLORS) {
          return renderColorFilter(filter)
        }
        return renderCheckboxFilter(filter)

      default:
        return null
    }
  }

  if (showShimmer) {
    return <Skeleton className={styles.filterWithSidebarSkeleton} />
  }

  return (
    <div
      className={classNames(
        styles.sidebarContainer,
        isHeaderVisible ? styles.sidebarContainerHeaderVisible : styles.sidebarContainerHeaderHidden,
        totalSelectedFilters > 0 && 'has-selected-filters',
        className,
      )}
      ref={filterSectionRef}
    >
      <div className={styles.sidebarHeaderContainer}>
        <div className={styles.sidebarHeader}>
          <div className={styles.sidebarTitle}>Filter {totalSelectedFilters > 0 && `(${totalSelectedFilters})`}</div>
        </div>

        {selectedFilterChips.length > 0 && (
          <div className={styles.selectedFiltersSection}>
            <ChipGroup
              chips={selectedFilterChips.map(chip => ({
                label: chip.label,
                closeable: true,
                key: `${chip.filterType}-${chip.value}`,
                value: chip.value,
              }))}
              onChipDismiss={handleChipDismissByLabel}
              clearAll={false}
              className={styles.selectedFiltersChips}
            />
            {totalSelectedFilters >= CLEAR_ALL_THRESHOLD && (
              <button className={styles.clearAllButton} onClick={clearAllFilters}>
                {CLEAR_ALL_TEXT}
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.filtersSection}>
        <FilterCollapseGroup
          filters={filters}
          selectedFiltersState={selectedFiltersState}
          activeItems={activeFilters}
          onChange={handleFilterCollapseChange}
          renderFilterComponent={renderFilterComponent}
          isMobile={false}
          className={styles.sidebarCollapseContainer}
        />
      </div>
    </div>
  )
}
